import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Chat } from "snudbud-backbone-sdk";
import handleError from "../utils/handleError";
import { useNotificationSound } from "../utils/useNotificationSound";
import { useTitleNotification } from "../utils/useTitleNotification";
import { useAPI } from "./APIContext";

interface Notification {
  id: string;
  message: Chat.Message;
  userId: string;
  sender: string;
  profileImage?: string;
  isVisible: boolean;
}

interface NotificationPreferences {
  enableSound: boolean;
  enableTitleNotifications: boolean;
  enableBrowserNotifications: boolean;
}

interface NotificationContextType {
  unreadCount: number;
  fetchUnreadCount: () => Promise<void>;
  clearUnreadCount: () => void;
  notifications: Notification[];
  notificationQueue: Notification[];
  preferences: NotificationPreferences;
  updatePreferences: (newPreferences: Partial<NotificationPreferences>) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

const MAX_VISIBLE_NOTIFICATIONS = 3;
const LOCAL_STORAGE_KEY = "snudbud_notification_preferences";

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { api, user } = useAPI();
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { userId } = useParams<{ userId?: string }>();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationQueue, setNotificationQueue] = useState<Notification[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    enableSound: true,
    enableTitleNotifications: true,
    enableBrowserNotifications: false,
  });

  // Initialize notification sound
  const notificationSound = useNotificationSound({
    isEnabled: preferences.enableSound,
  });

  // Initialize title notification
  const titleNotification = useTitleNotification({
    isEnabled: preferences.enableTitleNotifications,
  });

  // Reference to the original page title
  const originalTitle = useRef<string>(document.title);

  // Update notification settings when preferences change
  useEffect(() => {
    notificationSound.setEnabled(preferences.enableSound);
    titleNotification.setEnabled(preferences.enableTitleNotifications);

    // Request permission for browser notifications if enabled
    if (preferences.enableBrowserNotifications) {
      requestNotificationPermission();
    }

    // Save to localStorage
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  // Load preferences on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (e) {
        console.error("Failed to parse notification preferences", e);
      }
    }

    // Store original title
    originalTitle.current = document.title;
  }, []);

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      return;
    }

    if (Notification.permission !== "granted" && Notification.permission !== "denied") {
      try {
        await Notification.requestPermission();
      } catch (error) {
        console.error("Error requesting notification permission:", error);
      }
    }
  };

  const updatePreferences = (newPreferences: Partial<NotificationPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      ...newPreferences,
    }));
  };

  const fetchUnreadCount = async () => {
    try {
      const stats = await api.chat.stat();
      setUnreadCount(stats.unreadMessages || 0);

      // Update title notification count if enabled
      if (preferences.enableTitleNotifications) {
        titleNotification.updateCount(stats.unreadMessages || 0);
      } else {
        titleNotification.stopBlinking();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const clearUnreadCount = () => {
    setUnreadCount(0);
    if (preferences.enableTitleNotifications) {
      titleNotification.updateCount(0);
    } else {
      titleNotification.stopBlinking();
    }
  };

  // Process queue and show notifications
  useEffect(() => {
    if (notifications.length < MAX_VISIBLE_NOTIFICATIONS && notificationQueue.length > 0) {
      // Move from queue to visible notifications
      const nextNotification = notificationQueue[0];
      const updatedQueue = notificationQueue.slice(1);

      setNotificationQueue(updatedQueue);
      setNotifications([...notifications, { ...nextNotification, isVisible: true }]);
    }
  }, [notifications, notificationQueue]);

  // Initial fetch of unread count
  useEffect(() => {
    fetchUnreadCount();
  }, []);

  // Show browser notification
  const showBrowserNotification = (notification: Notification) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    try {
      const options = {
        body: notification.message.content || "New audio message",
        icon: notification.profileImage || "/logo.png",
        tag: notification.userId, // Ensures only one notification per sender
      };

      const browserNotification = new Notification(`${notification.sender}: New Message`, options);

      // Handle notification click
      browserNotification.onclick = () => {
        window.focus();
        window.location.href = `/chat/${notification.userId}`;
        browserNotification.close();
      };

      // Auto close after 5 seconds
      setTimeout(() => browserNotification.close(), 5000);
    } catch (error) {
      console.error("Error showing browser notification:", error);
    }
  };

  // Listen for new messages
  useEffect(() => {
    if (!api.socket) return;

    const handleIncomingMessage = async (message: Chat.Message) => {
      // Update unread count
      await fetchUnreadCount();

      // If we're not in the chat page with this user, show notification
      const isChatOpen = location.pathname.includes("/chat/") && userId === message.fromUserId;

      if (!isChatOpen && message.fromUserId !== user?.userId) {
        // Play notification sound if enabled
        if (preferences.enableSound) {
          notificationSound.play();
        }

        // Try to get sender info
        let senderName = "Someone";
        let profileImage = "";
        try {
          const profile = await api.user.fetchProfile({ userId: message.fromUserId });
          senderName = profile.name;
          profileImage = profile.profileImageUrl || "";
        } catch (error) {
          // Ignore error
        }

        const newNotification: Notification = {
          id: Date.now().toString(),
          message,
          userId: message.fromUserId,
          sender: senderName,
          profileImage,
          isVisible: false,
        };

        // Show browser notification if enabled and document is hidden
        if (preferences.enableBrowserNotifications && document.hidden) {
          showBrowserNotification({ ...newNotification, isVisible: true });
        }

        // Add to visible notifications or queue based on current count
        if (notifications.length < MAX_VISIBLE_NOTIFICATIONS) {
          setNotifications((prev) => [...prev, { ...newNotification, isVisible: true }]);

          // Auto-dismiss notification after 5 seconds
          setTimeout(() => {
            dismissNotification(newNotification.id);
          }, 5000);
        } else {
          setNotificationQueue((prev) => [...prev, newNotification]);
        }
      }
    };

    api.socket.on("chat:message", handleIncomingMessage);

    return () => {
      api.socket?.off("chat:message", handleIncomingMessage);
    };
  }, [api.socket, location.pathname, userId, user, notifications, notificationQueue, preferences]);

  // Update unread count when navigating to chat page (messages will be marked as read)
  useEffect(() => {
    if (location.pathname.includes("/chat/")) {
      fetchUnreadCount();
    }
  }, [location.pathname]);

  const dismissNotification = (id: string) => {
    setNotifications((prev) => {
      const updatedNotifications = prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n));

      // After 300ms, remove the notification completely
      setTimeout(() => {
        setNotifications((currentNotifications) => currentNotifications.filter((n) => n.id !== id));
      }, 300);

      return updatedNotifications;
    });
  };

  // Mark all notifications as read when user switches tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && location.pathname.includes("/chat/")) {
        fetchUnreadCount();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location.pathname]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        fetchUnreadCount,
        clearUnreadCount,
        notifications,
        notificationQueue,
        preferences,
        updatePreferences,
      }}
    >
      {children}
      <div className="fixed bottom-0 right-0 z-50 p-4 flex flex-col items-end space-y-3 md:space-y-2 w-full md:w-auto">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`transform transition-all duration-300 ease-in-out 
              ${notification.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
              w-full md:w-auto max-w-full md:max-w-sm`}
          >
            <div className="bg-white rounded-xl shadow-lg overflow-hidden w-full border border-gray-100 hover:shadow-xl transition-shadow duration-200">
              <div className="p-3 md:p-4">
                {/* Notification header with sender info */}
                <div className="flex items-center mb-2">
                  {notification.profileImage ? (
                    <img
                      src={notification.profileImage}
                      alt={notification.sender}
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border border-gray-100"
                    />
                  ) : (
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium">
                      {notification.sender?.charAt(0) || "?"}
                    </div>
                  )}
                  <div className="ml-2 md:ml-3 flex-1">
                    <p className="font-semibold text-gray-800 text-sm md:text-base">{notification.sender}</p>
                    <p className="text-xs text-gray-500 flex items-center">
                      <span className="w-2 h-2 rounded-full bg-primary mr-1.5 animate-pulse"></span>
                      New message
                    </p>
                  </div>
                  <button
                    onClick={() => dismissNotification(notification.id)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors duration-200"
                    aria-label="Close notification"
                  >
                    <svg className="h-3 w-3 md:h-4 md:w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                {/* Message content with improved styling */}
                <div className="bg-gray-50 p-2 md:p-3 rounded-lg text-gray-700 text-xs md:text-sm leading-relaxed mb-2">
                  {notification.message.content ? (
                    notification.message.content.length > 100 ? (
                      `${notification.message.content.substring(0, 100)}...`
                    ) : (
                      notification.message.content
                    )
                  ) : (
                    <span className="text-gray-500 italic flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                        />
                      </svg>
                      Audio message
                    </span>
                  )}
                </div>

                {/* Action button with improved styling */}
                <div className="flex justify-end">
                  <a
                    href={`/chat/${notification.userId}`}
                    onClick={() => dismissNotification(notification.id)}
                    className="bg-primary text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium hover:bg-opacity-90 transition-colors duration-200 flex items-center group"
                  >
                    <span>Reply</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 md:h-4 md:w-4 ml-1.5 transform transition-transform duration-200 group-hover:translate-x-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
        {notificationQueue.length > 0 && notifications.length >= MAX_VISIBLE_NOTIFICATIONS && (
          <div className="bg-primary bg-opacity-90 text-white text-xs rounded-full px-4 py-2 self-end animate-pulse flex items-center shadow-lg">
            <span className="mr-2">{notificationQueue.length}</span>
            <span>more {notificationQueue.length === 1 ? "message" : "messages"}</span>
          </div>
        )}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === null) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};
